/**
 * Module dependencies.
 */

import { GREEN_DARK, Logo, UserBadge, getAuthUser } from 'app/components/pages/landing/brand';
import { SiteFooter } from 'app/components/pages/landing/site-footer';
import { appRoutes } from 'app/routes';
import { useNavigate } from 'react-router-dom';
import React, { useMemo } from 'react';

/**
 * Document revision date (update when the policies change).
 */

const LAST_UPDATED = '1 August 2026';

/**
 * Typography helpers.
 */

function Section({ children, title }: { children: React.ReactNode; title: string }): JSX.Element {
  return (
    <section className={'mt-10'}>
      <h2 className={"font-['Outfit'] text-xl font-semibold tracking-[-0.02em] text-neutral-900"}>{title}</h2>

      <div className={'mt-3 space-y-3 text-[15px] leading-relaxed text-neutral-600'}>{children}</div>
    </section>
  );
}

function Bullets({ items }: { items: string[] }): JSX.Element {
  return (
    <ul className={'m-0 list-none space-y-2 p-0'}>
      {items.map(item => (
        <li className={'flex gap-2.5'} key={item}>
          <span aria-hidden className={'mt-2 h-1.5 w-1.5 shrink-0 rounded-full'} style={{ background: GREEN_DARK }} />

          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Legal page layout — shared chrome (nav, background, footer) for all policies.
 */

function LegalLayout({
  children,
  intro,
  title
}: {
  children: React.ReactNode;
  intro: string;
  title: string;
}): JSX.Element {
  const navigate = useNavigate();
  const authUser = useMemo(() => getAuthUser(), []);

  return (
    <div
      className={"relative isolate flex min-h-screen flex-col overflow-x-hidden font-['Roboto'] text-[#0A0A0B]"}
      style={{ backgroundColor: '#F4F4F6' }}
    >
      {/* Decorative background */}
      <div
        aria-hidden
        className={'pointer-events-none absolute inset-0 -z-10'}
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(10,10,11,0.16) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
          maskImage: 'radial-gradient(90% 40% at 50% 0%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(90% 40% at 50% 0%, #000 30%, transparent 100%)'
        }}
      />

      <div
        aria-hidden
        className={'pointer-events-none absolute -left-40 -top-44 -z-10 h-[36rem] w-[36rem] rounded-full'}
        style={{
          backgroundImage: 'radial-gradient(circle at 40% 40%, rgba(34,165,68,0.28), transparent 70%)',
          filter: 'blur(100px)'
        }}
      />

      {/* Nav */}
      <header
        className={'sticky top-0 z-20 border-b'}
        style={{
          backdropFilter: 'blur(18px) saturate(1.6)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.6)',
          backgroundColor: 'rgba(255,255,255,0.62)',
          borderColor: 'rgba(228,228,231,0.7)'
        }}
      >
        <div className={'mx-auto flex h-[72px] w-full max-w-5xl items-center justify-between px-6'}>
          <button aria-label={'Contab home'} onClick={() => navigate(appRoutes.home)} type={'button'}>
            <Logo />
          </button>

          {authUser ? (
            <UserBadge onClick={() => navigate(appRoutes.dashboard.home.base)} user={authUser} />
          ) : (
            <button
              className={
                'rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-800 shadow-sm transition-colors hover:bg-neutral-50'
              }
              onClick={() => navigate(appRoutes.login)}
              type={'button'}
            >
              {'Sign in'}
            </button>
          )}
        </div>
      </header>

      {/* Content */}
      <main className={'mx-auto w-full max-w-3xl flex-1 px-6 py-16 sm:py-20'}>
        <h1 className={"font-['Outfit'] text-4xl font-semibold tracking-[-0.03em] sm:text-5xl"}>{title}</h1>

        <p className={'mt-4 text-lg text-neutral-500'}>{intro}</p>

        <p className={'mt-2 text-sm text-neutral-400'}>{`Last updated: ${LAST_UPDATED}`}</p>

        {/* Document card */}
        <div
          className={
            'mt-8 rounded-3xl border border-white/60 p-8 backdrop-blur-xl sm:p-10 [&>section:first-child]:mt-0'
          }
          style={{
            backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.85), rgba(255,255,255,0.6))',
            boxShadow: '0 30px 70px -40px rgba(10,10,11,0.35), inset 0 1px 0 0 rgba(255,255,255,0.9)'
          }}
        >
          {children}
        </div>

        <div className={'mt-6 rounded-2xl border border-white/60 bg-white/60 p-6 backdrop-blur'}>
          <h2 className={"font-['Outfit'] text-lg font-semibold tracking-[-0.02em]"}>{'Questions?'}</h2>

          <p className={'mt-2 text-[15px] leading-relaxed text-neutral-600'}>
            {'If anything here is unclear, reach out to '}

            <a className={'font-medium'} href={'mailto:privacy@contab.app'} style={{ color: GREEN_DARK }}>
              {'privacy@contab.app'}
            </a>

            {' and we will be happy to help.'}
          </p>
        </div>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

/**
 * Export `TermsPage` component.
 */

export function TermsPage(): JSX.Element {
  return (
    <LegalLayout
      intro={
        'The terms below govern your use of Contab. By creating an account or using the service, you agree to them.'
      }
      title={'Terms & conditions'}
    >
      <Section title={'1. Acceptance of terms'}>
        <p>
          {
            'These terms form a binding agreement between you and Contab. If you do not agree with any part of them, please do not use the service.'
          }
        </p>
      </Section>

      <Section title={'2. Your account'}>
        <p>
          {
            'You are responsible for the activity that happens under your account and for keeping your sign-in credentials secure. Notify us immediately if you suspect any unauthorised access.'
          }
        </p>
      </Section>

      <Section title={'3. Acceptable use'}>
        <p>{'When using Contab you agree not to:'}</p>

        <Bullets
          items={[
            'Use the service for any unlawful purpose or in breach of any regulation.',
            'Attempt to gain unauthorised access to other accounts, systems or data.',
            'Interfere with, disrupt or place undue load on the service or its infrastructure.',
            'Reverse engineer or resell the service without our written permission.'
          ]}
        />
      </Section>

      <Section title={'4. Your data'}>
        <p>
          {
            'You retain ownership of the financial data you add to Contab. You grant us the limited rights needed to store, process and display that data so we can provide the service to you. See our Privacy policy for details.'
          }
        </p>
      </Section>

      <Section title={'5. Availability & changes'}>
        <p>
          {
            'We work hard to keep Contab available and accurate, but the service is provided “as is” without warranties of any kind. We may update, suspend or discontinue features, and we may revise these terms — we will post the updated date above when we do.'
          }
        </p>
      </Section>

      <Section title={'6. Limitation of liability'}>
        <p>
          {
            'Contab is a tool to help you organise your finances; it is not financial, tax or investment advice. To the extent permitted by law, we are not liable for any indirect or consequential loss arising from your use of the service.'
          }
        </p>
      </Section>

      <Section title={'7. Termination'}>
        <p>
          {
            'You may stop using Contab and delete your account at any time. We may suspend or terminate access if these terms are breached.'
          }
        </p>
      </Section>
    </LegalLayout>
  );
}

/**
 * Export `PrivacyPage` component.
 */

export function PrivacyPage(): JSX.Element {
  return (
    <LegalLayout
      intro={'This policy explains what personal data Contab collects, why, and the rights you have over it.'}
      title={'Privacy policy'}
    >
      <Section title={'1. Data we collect'}>
        <Bullets
          items={[
            'Account details: your name, email address and profile photo (e.g. from Google sign-in).',
            'Financial data you add: accounts, transactions, projects, budgets and notes.',
            'Technical data: basic device and usage information needed to run and secure the service.'
          ]}
        />
      </Section>

      <Section title={'2. How we use your data'}>
        <Bullets
          items={[
            'To provide the core service — showing your accounts, projects and insights.',
            'To authenticate you and keep your account secure.',
            'To maintain, troubleshoot and improve the product.',
            'To contact you about important changes to the service.'
          ]}
        />
      </Section>

      <Section title={'3. Legal basis'}>
        <p>
          {
            'We process your data to perform our contract with you (providing the service), to comply with legal obligations, and on the basis of our legitimate interest in operating and securing Contab.'
          }
        </p>
      </Section>

      <Section title={'4. Sharing'}>
        <p>
          {
            'We do not sell your personal data. We only share it with the service providers that help us run Contab (such as hosting and authentication), under agreements that require them to protect it, or where required by law.'
          }
        </p>
      </Section>

      <Section title={'5. Retention'}>
        <p>
          {
            'We keep your data for as long as your account is active. When you delete your account, we remove your personal data within a reasonable period, except where we must retain it to meet legal obligations.'
          }
        </p>
      </Section>

      <Section title={'6. Your rights'}>
        <p>{'Depending on your location, you have the right to:'}</p>

        <Bullets
          items={[
            'Access the personal data we hold about you.',
            'Correct data that is inaccurate or incomplete.',
            'Request deletion of your data.',
            'Export your data in a portable format.',
            'Object to or restrict certain processing.'
          ]}
        />
      </Section>

      <Section title={'7. Security'}>
        <p>
          {
            'We protect your data with encryption in transit, access controls and regular backups. See our Security page for more detail.'
          }
        </p>
      </Section>
    </LegalLayout>
  );
}

/**
 * Export `CookiePage` component.
 */

export function CookiePage(): JSX.Element {
  return (
    <LegalLayout
      intro={'Contab uses a small number of cookies to keep you signed in and to improve the product.'}
      title={'Cookie policy'}
    >
      <Section title={'1. What cookies are'}>
        <p>
          {
            'Cookies are small text files stored on your device when you visit a website. They let the site remember your session and preferences between visits.'
          }
        </p>
      </Section>

      <Section title={'2. Cookies we use'}>
        <Bullets
          items={[
            'Essential — session cookies that keep you securely signed in. The service does not work without them.',
            'Preferences — remember choices such as theme so the app feels consistent.',
            'Analytics — anonymous, aggregated usage data that helps us understand and improve the product.'
          ]}
        />
      </Section>

      <Section title={'3. Managing cookies'}>
        <p>
          {
            'You can control or delete cookies through your browser settings. Note that blocking essential cookies will stop you from being able to sign in and use Contab.'
          }
        </p>
      </Section>

      <Section title={'4. Changes'}>
        <p>{'If we change how we use cookies, we will update this page and the date shown above.'}</p>
      </Section>
    </LegalLayout>
  );
}

/**
 * Export `SecurityPage` component.
 */

export function SecurityPage(): JSX.Element {
  return (
    <LegalLayout
      intro={'Your financial data deserves serious protection. Here is how we keep Contab secure.'}
      title={'Security'}
    >
      <Section title={'Encryption'}>
        <p>
          {
            'All traffic between your browser and Contab is encrypted in transit with TLS. Sensitive credentials, such as passwords, are stored only as salted hashes — never in plain text.'
          }
        </p>
      </Section>

      <Section title={'Access control'}>
        <p>
          {
            'Access to production systems is restricted to authorised personnel and protected with strong authentication. Sessions are signed and scoped so that you only ever see your own data.'
          }
        </p>
      </Section>

      <Section title={'Backups'}>
        <p>
          {
            'Your data is backed up regularly so it can be restored in the event of a failure. Backups are protected with the same care as the live service.'
          }
        </p>
      </Section>

      <Section title={'Responsible disclosure'}>
        <p>
          {
            'We welcome reports from security researchers. If you believe you have found a vulnerability, please email us at '
          }

          <a className={'font-medium'} href={'mailto:security@contab.app'} style={{ color: GREEN_DARK }}>
            {'security@contab.app'}
          </a>

          {
            ' with the details so we can investigate. Please give us reasonable time to fix an issue before disclosing it publicly.'
          }
        </p>
      </Section>

      <Section title={'Your part'}>
        <p>
          {
            'Security is shared. Use a strong, unique password, keep your device up to date, and sign out on shared computers to help keep your account safe.'
          }
        </p>
      </Section>
    </LegalLayout>
  );
}
